<template>
  <div>
    <div class="loader" v-if="loading">
      <span aria-hidden="true" class="spinner-border" />
    </div>

    <div v-else>
      <div class="profile">
        <div class="profile__left">
          <div class="profile__title">Create Statement</div>
          <div class="profile__caption">
            You can create a new statement here
          </div>
        </div>
        <div class="profile__right">
          <div class="form__flex">
            <div class="form__flex__item">
              <label class="form__label" for>Choose Bank</label>

              <SelectSearch
                :options="
                  bankList.map(({ text, value, ...data }) => ({
                    value,
                    text,
                    data,
                  }))
                "
                :selected-option="{
                  value: statementForm.bank,
                  text: getBankName(statementForm.bank),
                }"
                :on-option-selected="(value) => selectBank(value)"
                placeholder=""
              />

              <span class="attention" v-if="$v.statementForm.bank.$error"
                >Please select a bank</span
              >
            </div>
          </div>

          <div class="form__flex">
            <div class="form__flex__item">
              <label class="form__label form__label--2" for>
                <span>Unique Identifier</span>
              </label>

              <input
                class="form__input"
                type="text"
                v-model.trim="$v.statementForm.uniqueIdentifier.$model"
              />

              <span
                class="attention"
                v-if="$v.statementForm.uniqueIdentifier.$error"
                >Please provide a session ID</span
              >
            </div>
          </div>

          <div class="form__flex">
            <div class="form__flex__item">
              <label class="form__label form__label--2" for>
                <span>Transaction Reference</span>
              </label>

              <input
                class="form__input"
                type="text"
                v-model.trim="$v.statementForm.reference.$model"
              />
              <span class="attention" v-if="$v.statementForm.reference.$error"
                >Please provide a transaction reference</span
              >
            </div>
          </div>

          <div class="form__flex">
            <div class="form__flex__item">
              <label class="form__label form__label--2" for>
                <span>Narration</span>
              </label>
              <text-area
                label="Add a note"
                class="form__input"
                :handle-text-area="(val) => (statementForm.narration = val)"
              />
            </div>
          </div>

          <!--further details Section-->
          <div class="ml-3 d-flex justify-content-between mb-3">
            <div class="form__flex w-48">
              <div class="form__flex__item ml-0">
                <label class="form__label" for>Amount</label>
                <input
                  class="form__input"
                  type="text"
                  v-model.trim="$v.statementForm.amount.$model"
                  v-money="mask"
                />
                <span
                  class="attention"
                  v-if="$v.statementForm.amount.$error && submitAttempted"
                  >Please specify an amount</span
                >
              </div>
            </div>

            <div
              class="form__flex__item w-48"
              :class="{ 'mb-0': !!selectedReconciliationStatus }"
            >
              <label class="form__label" for>Reconciliation Status</label>

              <SelectSearch
                :options="
                  reconciliationStatus.map(({ text, value }) => ({
                    value,
                    text,
                  }))
                "
                :selected-option="{
                  value: statementForm.status,
                  text: statementForm.status,
                }"
                :on-option-selected="(value) => (statementForm.status = value)"
                placeholder=""
              />

              <span class="attention" v-if="$v.statementForm.status.$error"
                >Please specify a status</span
              >
            </div>
          </div>

          <!--date-->

          <div class="ml-3 d-flex justify-content-between mb-3">
            <div class="w-48 input-date" id="value-date">
              <div class="form__flex__item ml-0">
                <label class="form__label" for>Value Date</label>

                <calender
                  dateFormat="D MMMM, YYYY"
                  class="no-border"
                  direction="left"
                  input-class="no-border form-input icon-auto"
                  @change="
                    ({ endDate }) =>
                      (statementForm.valueDate = new Date(endDate).toString())
                  "
                />
                <span class="attention" v-if="$v.statementForm.valueDate.$error"
                  >Required*</span
                >
              </div>
            </div>

            <div class="w-48 input-date">
              <div class="form__flex__item ml-0" id="transaction-date">
                <label class="form__label" for>Transaction Date</label>

                <calender
                  dateFormat="D MMMM, YYYY"
                  class="no-border"
                  direction="left"
                  input-class="no-border form-input icon-auto"
                  @change="
                    ({ endDate }) =>
                      (statementForm.transactionDate = new Date(
                        endDate
                      ).toString())
                  "
                />
                <span
                  class="attention"
                  v-if="$v.statementForm.transactionDate.$error"
                  >Required*</span
                >
              </div>
            </div>
          </div>

          <!--time-->
          <div class="ml-3 d-flex justify-content-between mb-3">
            <div class="w-48">
              <div class="form__flex__item ml-0 mb-0">
                <label class="form__label" for>Value Time</label>
                <input
                  class="form__input input-time"
                  type="time"
                  value="00:00"
                  v-model="statementForm.valueTime"
                />
                <span class="attention" v-if="$v.statementForm.valueTime.$error"
                  >Required*</span
                >
              </div>
            </div>
            <div class="w-48">
              <div class="form__flex__item ml-0 mb-0">
                <label class="form__label" for>Transaction Time</label>
                <input
                  class="form__input input-time"
                  type="time"
                  value="00:00"
                  v-model="statementForm.transactionTime"
                />
                <span
                  class="attention"
                  v-if="$v.statementForm.transactionTime.$error"
                  >Required*</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="action">
        <button :disabled="submitting" @click="submit" class="btn btn--primary">
          {{ submitting ? "Processing..." : "Create settlement" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatAmount, formatMoneyMask } from "../../../../lib/formatAmount";
import { required, minLength } from "vuelidate/lib/validators";
import moment from "moment";

import {
  reactive,
  onMounted,
  computed,
  watch,
  toRefs,
  getCurrentInstance,
} from "@vue/composition-api";
import TextArea from "../../../../components/textarea";
import DatePicker from "../../../../components/datepicker";
import SelectSearch from "../../../../components/selectsearch";
import EmptyState from "../../../../components/empty-state";
import Calender from "../../../../components/calender";

export default {
  name: "CreateSingleStatement",

  components: {
    Calender,
    EmptyState,
    SelectSearch,
    DatePicker,
    TextArea,
  },
  metaInfo: {
    title: "Statements - Single",
  },

  validations: {
    statementForm: {
      bank: {
        required,
      },
      uniqueIdentifier: {
        required,
      },
      reference: {
        required,
      },
      amount: {
        notEmpty: (value) => value !== "₦ 0.00",
      },
      status: {
        required,
      },
      valueDate: {
        required,
      },
      transactionDate: {
        required,
      },
      valueTime: {
        required,
      },
      transactionTime: {
        required,
      },
    },

    payoutPin: {
      required,
      minLength: minLength(4),
    },
  },

  setup(props, { root }) {
    const store = root.$store;
    const vueInstance = getCurrentInstance().proxy;
    const mask = {
      decimal: ".",
      thousands: ",",
      prefix: "₦ ",
      suffix: "",
      precision: 2,
      masked: false /* doesn't work with directive */,
    };

    // data
    const data = reactive({
      statementForm: {
        bank: "",
        uniqueIdentifier: "",
        reference: "",
        narration: "",
        amount: "₦ 0.00",
        status: "",
        valueDate: "",
        transactionDate: "",
        valueTime: "",
        transactionTime: "",
      },
      reconciliationStatus: [
        {
          text: "Reconciled",
          value: "RECONCILED",
        },
        {
          text: "Reversal",
          value: "REVERSAL",
        },
        {
          text: "Unreconciled",
          value: "UNRECONCILED",
        },
      ],
      selectedReconciliationStatus: null,
      selectedBank: null,

      showModal: false,
      submitAttempted: false,
      submitting: false,
      loading: false,
    });

    //mounted
    onMounted(async () => {
      store.dispatch("setCustomMetaOptions", {
        title: "Create Settlement",
      });
    });

    //watchers
    watch(
      () => root.$route.fullPath,
      () => {}
    );

    const userDetails = computed(() => store.getters.getUserDetails);
    const bankList = computed(() => {
      return store.getters.getBankListKeyAndValuePair.map((bank) => {
        return {
          ...bank,
          value: bank.id,
          text: bank.value,
        };
      });
      // }
    });

    //methods
    const getBankName = (code) => {
      return store.getters.getBankName(code);
    };

    const selectBank = (bank) => {
      data.statementForm.bank = bank.value;
    };

    const submit = async () => {
      data.submitAttempted = true;
      vueInstance.$v.statementForm.$touch();
      if (vueInstance.$v.statementForm.$error) return;

      data.submitting = true;
      let payload = {
        bank_code: data.statementForm.bank,
        transaction_reference: data.statementForm.reference,
        unique_identifier: data.statementForm.uniqueIdentifier,
        reconciliation_status: data.statementForm.status.value,
        value_date: `${moment(data.statementForm.valueDate).format(
          "YYYY-MM-DD"
        )} ${data.statementForm.valueTime}:00`,

        transaction_date: `${moment(data.statementForm.transactionDate).format(
          "YYYY-MM-DD"
        )} ${data.statementForm.transactionTime}:00`,
        narration: data.statementForm.narration,
        amount: Number(formatMoneyMask(data.statementForm.amount, false)),
      };

      let res, error;

      [res, error] = await store.dispatch("createSingleStatement", payload);

      data.submitting = false;
      if (res) {
        store.dispatch("notify", {
          s: true,
          str: res.message,
        });
        await root.$options.router.push({ name: "statements-list" });
      } else {
        store.dispatch("notify", {
          e: true,
          str: error,
        });
      }
    };

    const clearBeneficiaryForm = () => {
      data.statementForm = {
        bank: "",
        uniqueIdentifier: "",
        reference: "",
        narration: "",
        amount: "₦ 0.00",
        status: "",
        valueDate: "",
        transactionDate: "",
        valueTime: "",
        transactionTime: "",
      };
      vueInstance.$v.statementForm.$reset();
    };

    return {
      ...toRefs(data),
      userDetails,
      selectBank,
      moment,
      getBankName,
      bankList,
      clearBeneficiaryForm,
      formatAmount,
      submit,
      mask,
    };
  },
};
</script>

<style lang="scss">
.profile {
  padding-left: 76px;
  border-top: none;

  &__title {
    font-style: normal;
    font-weight: 500;
    font-size: 24px;
    line-height: 27px;
    color: #7a153c;
  }
}

.text-area {
  border: none !important;
  resize: none;
  margin-bottom: 0.8rem;
  height: 30px !important;
}

.input-date {
  position: relative;
  .form__item {
    margin-bottom: 0;
  }
}

.form__flex__dropdown {
  .drop-down-component {
    margin-bottom: 0;

    .drop-down {
      width: 100%;
      padding-top: 2rem;
      padding-bottom: 2.5rem;
    }

    .dropdown-options {
      overflow-y: auto;
      height: 100%;
      min-height: 12rem;
    }
  }
}

.sudo-modal {
  min-width: 50% !important;
  display: block !important;
  padding-bottom: 0 !important;

  &__content {
    display: block !important;
    position: relative;

    .form__flex__item {
      background: #ebf0f4;
      text-align: left;
    }

    .dropdown-options {
      overflow-y: auto;
      height: 100%;
      min-height: 12rem;
    }
  }

  .modal-body {
    justify-content: center !important;

    h2 {
      font-weight: 500;
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 14px;
    }

    p {
      margin-bottom: 31px;
      color: #616e7c;
    }

    svg {
      margin-bottom: 12px;
    }

    textarea {
      resize: none;
      height: 4rem;
    }

    .boxed {
      width: 60%;
      margin: 0.5rem auto 1.5rem;

      .form__flex__item,
      button {
        margin-left: 0;
        width: 100%;
      }

      .details__card__item {
        padding: 0 0 24px;
        border: none;
      }
    }
  }
}
//for the datepicker
.w-100 {
  div input {
    min-width: 100% !important;
  }
}

#value-date {
  .daterangepicker.ltr.show-calendar {
    right: 125%;
    bottom: -200%;

    &::before,
    &::after {
      display: none;
    }
  }
}

#transaction-date {
  .daterangepicker.ltr.show-calendar {
    right: 235%;
    bottom: -200%;
    &::before,
    &::after {
      display: none;
    }
  }
}
</style>

<style lang="scss" scoped>
.bg-disabled {
  background: #ebf0f4;
}

.w-48 {
  width: 48%;
}

.balance {
  color: #261940;
  font-size: 1rem;
  line-height: 1.25rem;
  margin: 16px 0 16px 20px;
}

.form__flex__item {
  .input-time {
    padding-bottom: 7px;
  }
}

.form__flex__dropdown {
  margin-left: 16px;
  margin-bottom: 32px;
  width: 100%;
  position: relative;
}

.toggle {
  display: flex;
  align-items: center;
  // background: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), #261940;
  border-radius: 100px;
  // padding: 12px 18px;
  &.toggle-danger {
    .toggleWrapper {
      background: #e4ebf6 !important;
    }

    .toggle__text {
      color: #000000 !important;
    }
  }

  .live {
    background: #e90c0c !important;
  }

  .toggleWrapper {
    background: red;
    border-radius: 20px;
    padding: 1px;
    height: 20px;
    width: 35px;
  }

  &__text {
    color: #e90c0c;
    margin-left: 5px;
    font-size: 14px;
  }

  &__display {
    font-size: 12px;
    font-weight: 600;
    margin-right: 8px;
  }
}

.beforeAnimation {
  transition: 0.2s cubic-bezier(0.24, 0, 0.5, 1);
}

.afterAnimation {
  box-shadow: 0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 0px 0 hsla(0, 0%, 0%, 0.04),
    0 4px 9px hsla(0, 0%, 0%, 0.13), 0 3px 3px hsla(0, 0%, 0%, 0.05);
  transition: 0.35s cubic-bezier(0.54, 1.6, 0.5, 1);
}

.toggleWrapper input.mobileToggle {
  opacity: 0;
  position: absolute;
}

.toggleWrapper input.mobileToggle + label {
  position: relative;
  display: inline-block;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  transition: 0.4s ease;
  width: 30px;
  height: 15px;
  border-radius: 60px;
}

.toggleWrapper input.mobileToggle + label:before {
  content: "";
  position: absolute;
  display: block;
  transition: 0.2s cubic-bezier(0.24, 0, 0.5, 1);
  height: 18px;
  width: 32px;
  top: 0;
  left: 0;
  border-radius: 30px;
  background-color: rgba(#ffffff, 0.2);
  cursor: pointer;
}

.toggleWrapper input.mobileToggle + label:after {
  content: "";
  position: absolute;
  display: block;
  box-shadow: 0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 0px 0 hsla(0, 0%, 0%, 0.04),
    0 4px 9px hsla(0, 0%, 0%, 0.13), 0 3px 3px hsla(0, 0%, 0%, 0.05);
  transition: 0.35s cubic-bezier(0.54, 1.6, 0.5, 1);
  background: #ffffff;
  box-shadow: 0px 2px 2px rgba(45, 45, 45, 0.2);
  height: 18px;
  width: 18px;
  top: -0px;
  left: -2px;
  border-radius: 60px;
  cursor: pointer;
}

.toggleWrapper input.mobileToggle:checked + label:before {
  // background: #67d495;
  transition: width 0.2s cubic-bezier(0, 0, 0, 0.1);
}

.toggleWrapper input.mobileToggle:checked + label:after {
  left: 16px;
}

.pending-svg {
  height: 127px;
  width: 133px;
  margin-bottom: 1.15rem !important;
}
</style>
